import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent, type Editor as TiptapEditor } from '@tiptap/react';
import { Mark, mergeAttributes, type JSONContent } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Heading1,
  Heading2,
  List as ListIcon,
  ListOrdered,
  Code as CodeIcon,
  Code2,
  Quote as QuoteIcon,
  Link as LinkIcon,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { api } from '@/lib/api';

/**
 * Self-contained Link mark.
 *
 * `@tiptap/extension-link` is NOT in the foundation-declared dependency set, so
 * we define a minimal link mark using `@tiptap/core` (which ships transitively
 * with the declared TipTap packages). It renders as a safe `<a rel/target>` and
 * is toggled with the built-in `setMark('link')` / `unsetMark('link')` commands
 * — no module augmentation needed.
 */
const Link = Mark.create({
  name: 'link',
  priority: 1000,
  keepOnSplit: false,
  inclusive: false,
  addAttributes() {
    return {
      href: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'a[href]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        rel: 'noopener noreferrer nofollow',
        target: '_blank',
      }),
      0,
    ];
  },
});

/** Extract image files from a paste/drop payload. */
function imageFilesFrom(data: DataTransfer | null): File[] {
  if (!data) return [];
  const out: File[] = [];
  const files = data.files;
  for (let i = 0; i < files.length; i++) {
    const f = files.item(i);
    if (f && f.type.startsWith('image/')) out.push(f);
  }
  return out;
}

/** Swap the `src` of the image node currently holding `from` → `to` (or delete on empty `to`). */
function replaceImageSrc(editor: TiptapEditor, from: string, to: string): void {
  const { state, view } = editor;
  let tr = state.tr;
  let changed = false;
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && node.attrs.src === from) {
      if (to) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: to });
      } else {
        tr = tr.delete(pos, pos + node.nodeSize);
      }
      changed = true;
      return false;
    }
    return true;
  });
  if (changed) view.dispatch(tr);
}

export interface EditorProps {
  initialContent?: JSONContent | null;
  onChange?: (json: JSONContent) => void;
  onSave?: () => void;
}

export default function Editor({ initialContent, onChange, onSave }: EditorProps) {
  const editorRef = useRef<TiptapEditor | null>(null);
  const onSaveRef = useRef<EditorProps['onSave']>(onSave);
  const onChangeRef = useRef<EditorProps['onChange']>(onChange);
  const [uploading, setUploading] = useState(0);

  useEffect(() => {
    onSaveRef.current = onSave;
    onChangeRef.current = onChange;
  }, [onSave, onChange]);

  const uploadAndInsert = useCallback(async (file: File) => {
    const editor = editorRef.current;
    if (!editor) return;
    const localUrl = URL.createObjectURL(file);
    editor.chain().focus().setImage({ src: localUrl }).run();
    setUploading((n) => n + 1);
    try {
      const { url } = await api.upload(file);
      replaceImageSrc(editor, localUrl, url);
    } catch {
      // Upload failed — drop the temporary preview so we never persist a blob: URL.
      replaceImageSrc(editor, localUrl, '');
    } finally {
      URL.revokeObjectURL(localUrl);
      setUploading((n) => Math.max(0, n - 1));
    }
  }, []);

  const uploadAndInsertRef = useRef(uploadAndInsert);
  useEffect(() => {
    uploadAndInsertRef.current = uploadAndInsert;
  }, [uploadAndInsert]);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: 'Type here…' }),
    ],
    content: initialContent ?? undefined,
    editorProps: {
      attributes: {
        class: 'tiptap min-h-[60vh] max-w-none py-2 focus:outline-none',
      },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
          event.preventDefault();
          onSaveRef.current?.();
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const files = imageFilesFrom(event.clipboardData);
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => void uploadAndInsertRef.current(f));
        return true;
      },
      handleDrop: (_view, event) => {
        const files = imageFilesFrom(event.dataTransfer);
        if (files.length === 0) return false;
        event.preventDefault();
        files.forEach((f) => void uploadAndInsertRef.current(f));
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current?.(ed.getJSON());
    },
  });

  editorRef.current = editor;

  const pickImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = () => {
      const list = input.files;
      if (!list) return;
      for (let i = 0; i < list.length; i++) {
        const f = list.item(i);
        if (f) void uploadAndInsertRef.current(f);
      }
    };
    input.click();
  }, []);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = (editor.getAttributes('link').href as string | undefined) ?? '';
    const url = window.prompt('Link URL', prev);
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetMark('link').run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setMark('link', { href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="flex flex-col gap-2">
      <Toolbar editor={editor} uploading={uploading} onLink={setLink} onImage={pickImage} />
      <EditorContent editor={editor} />
    </div>
  );
}

/* ------------------------------- Toolbar ------------------------------- */

interface ToolbarProps {
  editor: TiptapEditor;
  uploading: number;
  onLink: () => void;
  onImage: () => void;
}

function Toolbar({ editor, uploading, onLink, onImage }: ToolbarProps) {
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1 rounded-md border border-border bg-surface/90 px-1.5 py-1 backdrop-blur">
      <TBtn
        label="Bold"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon size={16} />
      </TBtn>
      <TBtn
        label="Italic"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon size={16} />
      </TBtn>

      <Divider />

      <TBtn
        label="Heading 1"
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 size={16} />
      </TBtn>
      <TBtn
        label="Heading 2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 size={16} />
      </TBtn>

      <Divider />

      <TBtn
        label="Bullet list"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <ListIcon size={16} />
      </TBtn>
      <TBtn
        label="Numbered list"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={16} />
      </TBtn>

      <Divider />

      <TBtn
        label="Inline code"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <CodeIcon size={16} />
      </TBtn>
      <TBtn
        label="Code block"
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code2 size={16} />
      </TBtn>
      <TBtn
        label="Quote"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <QuoteIcon size={16} />
      </TBtn>

      <Divider />

      <TBtn label="Link" active={editor.isActive('link')} onClick={onLink}>
        <LinkIcon size={16} />
      </TBtn>
      <TBtn label="Image" active={false} onClick={onImage}>
        <ImageIcon size={16} />
      </TBtn>

      {uploading > 0 && (
        <span className="ml-auto flex items-center gap-1.5 pr-1 text-xs text-ink-faint">
          <Loader2 size={13} className="animate-spin" />
          Uploading {uploading > 1 ? `${uploading} images…` : 'image…'}
        </span>
      )}
    </div>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />;
}

interface TBtnProps {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function TBtn({ label, active, onClick, children }: TBtnProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={clsx(
        'flex h-7 w-7 items-center justify-center rounded transition-colors',
        active
          ? 'bg-surface-3 text-ink'
          : 'text-ink-muted hover:bg-surface-3 hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
